/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: PinListFormView.tsx
 ??? Created: 2025-01-27
 ?? Description:
    Form view for creating and editing PIN Lists.
    Includes PIN member management with search functionality.

 ?? Notes:
    - Refactored from monolithic 915-line file
    - Uses hooks for state management
    - Uses PinMemberRow component for member rows
    - Maintains QuickSearchModal functionality

 ?? Last Updated: 2025-01-27 by Rodrigo Cuadra (Refactored)
----------------------------------------------------------------*/

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Shield, Plus, Search, Copy } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import FixedActionBar from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { usePinListData } from './hooks/usePinListData';
import { usePinListForm } from './hooks/usePinListForm';
import { PinMemberRow } from './components/PinMemberRow';
import { deletePinListById, navigateToPinListDuplicate } from './pinListsActions';
import { fetchPinLists } from './api/pinListsApi';
import { PinList } from './types';

export default function PinListFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess } = useNotifications();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Quick List states
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListItems, setQuickListItems] = useState<PinList[]>([]);
  const [initialItems, setInitialItems] = useState<PinList[]>([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isQuickSearching, setIsQuickSearching] = useState(false);

  const { isLoading, formData: loadedFormData, members: loadedMembers } = usePinListData(id, duplicateId);

  const {
    formData,
    members,
    errors,
    isSaving,
    hasChanges,
    handleChange,
    addMember,
    updateMember,
    removeMember,
    handleSubmit,
    handleCancel
  } = usePinListForm(loadedFormData, loadedMembers, id, isEditMode);

  // Enhanced save handler
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      return { id, ...formData, members };
    }

    return savedData;
  }, [handleSubmit, formData, members, isEditMode, id]);

  // Use reusable hook for save actions
  const modeForSave = isEditMode ? 'edit' : 'create';
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/applications/pin-list',
    newPath: '/pbx/applications/pin-list/new',
    mode: modeForSave,
    getEditPath: (savedData) => `/pbx/applications/pin-list/${savedData.id || savedData.uuid}`,
    getFormData: () => ({ ...formData, members }),
    storageKey: 'pinLists:saveAndNewData',
  });

  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) {
      return members;
    }

    const query = searchQuery.trim().toLowerCase();

    return members.filter((member) =>
      member.pin.toLowerCase().includes(query) ||
      member.description.toLowerCase().includes(query)
    );
  }, [members, searchQuery]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !id) return;

    setIsDeleting(true);
    try {
      await deletePinListById(id);

      showSuccess(t('pinLists.messages.deleteSuccess'));
      navigate('/pbx/applications/pin-list');
    } catch (err) {
      // Error handled by deletePinListById
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [id, isEditMode, navigate, showSuccess, t]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToPinListDuplicate(navigate, id);
  }, [isEditMode, id, navigate]);

  // Navigate to new
  const handleAddNew = useCallback(() => {
    navigate('/pbx/applications/pin-list/new');
  }, [navigate]);

  // Quick List loader
  const loadInitialItems = useCallback(async () => {
    if (initialItems.length > 0) return;

    setIsLoadingInitial(true);
    try {
      const result = await fetchPinLists({
        page: 1,
        limit: 100,
        sortBy: 'name',
        sortOrder: 'asc'
      });
      if (result.success && Array.isArray(result.data)) {
        setInitialItems(result.data);
        setQuickListItems(result.data);
      }
    } catch (error) {
      console.error('Error loading initial items:', error);
    } finally {
      setIsLoadingInitial(false);
    }
  }, [initialItems.length]);

  const loadQuickList = useCallback(
    async (search: string) => {
      const normalized = search.trim();

      if (!normalized || normalized.length < 2) {
        setQuickListItems(initialItems);
        return;
      }

      setIsQuickSearching(true);
      try {
        const result = await fetchPinLists({
          page: 1,
          limit: 50,
          sortBy: 'name',
          sortOrder: 'asc',
          search: normalized
        });
        if (result.success && Array.isArray(result.data)) {
          setQuickListItems(result.data);
        }
      } catch (error) {
        console.error('Error loading quick list items:', error);
      } finally {
        setIsQuickSearching(false);
      }
    },
    [initialItems]
  );

  useEffect(() => {
    if (showQuickList && initialItems.length === 0) {
      loadInitialItems();
    }
  }, [showQuickList, initialItems.length, loadInitialItems]);

  useEffect(() => {
    if (!showQuickList) return;

    const timer = setTimeout(() => {
      if (quickListSearch.trim().length >= 2) {
        loadQuickList(quickListSearch);
      } else {
        setQuickListItems(initialItems);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [quickListSearch, showQuickList, loadQuickList, initialItems]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <ModuleFormHeader
        icon={<Shield className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('pinLists.editPinList')} - ${formData.name || ''}` : t('pinLists.createPinList')}
        backButtonText={t('pinLists.actions.list')}
        onBack={() => navigate('/pbx/applications/pin-list')}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('pinLists.actions.quickList'),
            onClick: () => setShowQuickList(true)
          },
          // 2. Duplicate (edit mode only)
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('pinLists.actions.duplicate'),
              onClick: handleDuplicateFromForm
            }]
            : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('pinLists.actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      <div className="mt-8 space-y-6">
        <form onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}>
          <div className="bg-card p-6 rounded-lg border border-border space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
              <Label htmlFor="name">
                <SimpleTooltip content={t('pinLists.fields.name.tooltip')}>
                  <span>
                    {t('pinLists.fields.name.label')}
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                </SimpleTooltip>
              </Label>
              <div>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(event) => handleChange('name', event.target.value)}
                  placeholder={t('pinLists.fields.name.placeholder')}
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-sm text-red-500 mt-1">{errors.name}</p>
                )}
              </div>

              <Label htmlFor="description">
                <SimpleTooltip content={t('pinLists.fields.description.tooltip')}>
                  <span>{t('pinLists.fields.description.label')}</span>
                </SimpleTooltip>
              </Label>
              <div>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(event) => handleChange('description', event.target.value)}
                  placeholder={t('pinLists.fields.description.placeholder')}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between pt-4 border-t border-border">
                <h3 className="text-lg font-semibold">{t('pinLists.pins')}</h3>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder={t('pinLists.searchPins')}
                    className="pl-10"
                  />
                </div>
              </div>

              {errors.members && (
                <p className="text-sm text-red-500">{errors.members}</p>
              )}

              <table className="w-full border-collapse table-fixed">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-foreground" style={{ width: '35%' }}>
                      <SimpleTooltip content={t('pinLists.fields.pin.tooltip')}>
                        <span>{t('pinLists.fields.pin.label')}</span>
                      </SimpleTooltip>
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-foreground" style={{ width: '51%' }}>
                      <SimpleTooltip content={t('pinLists.fields.pinDescription.tooltip')}>
                        <span>{t('pinLists.fields.pinDescription.label')}</span>
                      </SimpleTooltip>
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-foreground" style={{ width: '14%' }}>
                      <SimpleTooltip content={t('pinLists.fields.enabled.tooltip')}>
                        <span>{t('pinLists.fields.enabled.label')}</span>
                      </SimpleTooltip>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-8 text-muted-foreground">
                        {members.length === 0
                          ? t('pinLists.messages.noPins')
                          : t('pinLists.messages.noResults')}
                      </td>
                    </tr>
                  ) : (
                    filteredMembers.map((member) => {
                      const actualIndex = members.findIndex((item) => item.tempId === member.tempId || (item.id && item.id === member.id));

                      return (
                        <PinMemberRow
                          key={member.tempId}
                          member={member}
                          index={actualIndex}
                          onUpdate={updateMember}
                          onRemove={removeMember}
                          errors={errors}
                        />
                      );
                    })
                  )}
                </tbody>
              </table>

              <div className="mt-4 flex justify-end">
                <Button type="button" onClick={addMember} size="sm" className="gap-2">
                  <Plus className="h-4 w-4" />
                  {t('pinLists.actions.addPin')}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
        isEditMode={isEditMode}
        isSaving={isSaving}
        deleteDisabled={isDeleting}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete')}
        useSaveDropdown={true}
        saveDropdownStorageKey="pinLists:saveAction"
        saveDisabled={isSaving}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('pinLists.messages.confirmDeleteTitle', 'Delete PIN List')}
        message={t('pinLists.messages.confirmDeleteMessage', { name: formData.name || 'PIN list' })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('pinLists.title')}
        items={quickListItems}
        isLoadingInitial={isLoadingInitial}
        isSearching={isQuickSearching}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/applications/pin-list/${item.uuid || item.id}`);
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            onClick={() => {
              if (!isCurrent) {
                setShowQuickList(false);
                setQuickListSearch('');
                navigate(`/pbx/applications/pin-list/${item.uuid || item.id}`);
              }
            }}
            disabled={isCurrent}
            className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'
              }`}
          >
            <div className="flex items-center gap-3">
              <Shield className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">
                    {item.name}
                  </span>
                  {isCurrent && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">
                      {t('pinLists.current', 'Current')}
                    </span>
                  )}
                </div>
                {item.description && (
                  <div className="text-sm text-muted-foreground truncate">
                    {item.description}
                  </div>
                )}
              </div>
            </div>
          </button>
        )}
        searchPlaceholder={t('common.searchPlaceholder')}
        minCharsMessage={t('pinLists.quickListMinChars', 'Type at least 2 characters to search')}
        emptyMessage={t('pinLists.quickListEmpty', 'Start typing to search PIN lists')}
        noResultsMessage={t('pinLists.quickListNoResults', 'No PIN lists found')}
        headerIcon={<Shield className="h-5 w-5" />}
        minSearchChars={2}
      />
    </div>
  );
}
